import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { Folder, FolderOpen, Check, Pin, Lock } from 'lucide-react'
import { useFolders } from '../../contexts/FoldersContext'
import SensitiveNotePasswordModal from './SensitiveNotePasswordModal'
import toast from 'react-hot-toast'

/**
 * Note Folder Dropdown Component
 * 
 * Dropdown for moving a note to different folders.
 * Shows current folder and allows selection of new folder.
 */

const NoteFolderDropdown = ({ 
  note, 
  onNoteMove,
  className = "" 
}) => {
  const { 
    folders, 
    moveNoteToFolder, 
    moveNoteToRoot, 
    requiresPasswordForMove,
    getPasswordOperationType,
    loading 
  } = useFolders()
  
  const [isOpen, setIsOpen] = useState(false)
  const [isMoving, setIsMoving] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [pendingMove, setPendingMove] = useState(null)
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0, width: 0 })
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const handleResize = () => {
      if (isOpen) {
        calculateButtonPosition()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('resize', handleResize)
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen])

  // Calculate button position for dropdown placement
  const calculateButtonPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const dropdownWidth = 192 // 192px = w-48 width
      const viewportWidth = window.innerWidth
      const margin = 16
      
      // Position from right edge of button, but ensure it doesn't go off-screen
      let left = rect.right - dropdownWidth
      
      // Adjust if dropdown would go off the left edge
      if (left < margin) {
        left = margin
      }
      
      // Adjust if dropdown would go off the right edge  
      if (left + dropdownWidth > viewportWidth - margin) {
        left = viewportWidth - dropdownWidth - margin
      }
      
      // Check if dropdown should appear above button (if not enough space below)
      const dropdownHeight = 240 // Approximate max height
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      
      let top = rect.bottom + window.scrollY + 4
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4
      }
      
      setButtonPosition({
        top: top,
        left: left,
        width: rect.width
      })
    }
  }

  /**
   * Handle folder selection
   */
  const handleFolderSelect = async (folderId, folderName) => {
    if (isMoving) return

    // Check if this move requires a password
    if (requiresPasswordForMove(note, folderId)) {
      const targetFolder = folderId ? folders.find(f => f.folderId === folderId) : null
      setPendingMove({ 
        folderId, 
        folderName: folderName || 'root',
        targetFolder,
        operationType: getPasswordOperationType(note, folderId)
      })
      setShowPasswordModal(true)
      setIsOpen(false)
      return
    }

    // Normal move (no password required)
    await executeMove(folderId)
  }

  /**
   * Execute the actual move operation
   */
  const executeMove = async (folderId, password = null) => {
    if (isMoving) return

    setIsMoving(true)
    try {
      if (folderId === null) {
        // Move to root
        await moveNoteToRoot(note.noteId, password)
      } else {
        // Move to specific folder
        await moveNoteToFolder(note.noteId, folderId, password)
      }
      
      setIsOpen(false)
      onNoteMove?.() // Callback to refresh notes list
      
    } catch (error) {
      console.error('Error moving note:', error)
      // Error handling is done in the context
    } finally {
      setIsMoving(false)
    }
  }

  /**
   * Handle password confirmation for sensitive folder moves
   */
  const handlePasswordConfirm = async (password) => {
    if (!pendingMove) return
    
    await executeMove(pendingMove.folderId, password)
    setPendingMove(null)
  }

  /**
   * Handle password modal close
   */
  const handlePasswordCancel = () => {
    setShowPasswordModal(false)
    setPendingMove(null)
  }



  /**
   * Get current folder info
   */
  const getCurrentFolder = () => {
    if (!note.folderId) return null
    return folders.find(f => f.folderId === note.folderId)
  }

  const currentFolder = getCurrentFolder()

  return (
    <div 
      ref={dropdownRef} 
      className="relative"
    >
      {/* Dropdown Toggle Button */}
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation()
          if (!isOpen) {
            calculateButtonPosition()
          }
          setIsOpen(!isOpen)
        }}
        disabled={isMoving || loading}
        className={className}
        title={`Move to folder (Currently: ${currentFolder?.name || 'No folder'})`}
      >
        {isMoving ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
        ) : currentFolder ? (
          <FolderOpen className="h-4 w-4 text-current" />
        ) : (
          <Folder className="h-4 w-4 text-current" />
        )}
      </button>

      {/* Dropdown Menu - Using Portal to escape overflow containers */}
      {isOpen && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[60]"
                     style={{
             top: `${buttonPosition.top}px`,
             left: `${buttonPosition.left}px`,
           }}
         >
          <div className="py-1 max-h-60 overflow-y-auto">
            {/* Current Folder Header */}
            {currentFolder && (
              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                Current: {currentFolder.name}
              </div>
            )}

            {/* Root Option - move to actual root (no folder) */}
            <button
              onClick={() => handleFolderSelect(null, 'No folder')}
              disabled={isMoving}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                !currentFolder ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <span className="flex items-center">
                <Folder className="h-4 w-4 mr-2" />
                No folder
              </span>
              {!currentFolder && <Check className="h-4 w-4" />}
            </button>

            {/* Separator */}
            {folders.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-700" />
            )}

            {/* Folder Options */}
            {folders.map(folder => (
              <button
                key={folder.folderId}
                onClick={() => handleFolderSelect(folder.folderId, folder.name)}
                disabled={isMoving}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-between ${
                  currentFolder?.folderId === folder.folderId 
                    ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <span className="flex items-center">
                  <span className="mr-2 flex items-center">{getFolderIcon(folder.folderType, folder.color)}</span>
                  <span 
                    className="font-medium"
                    style={{ color: folder.color }}
                  >
                    {folder.name}
                  </span>
                  {folder.isDefault && (
                    <span className="ml-1 text-xs text-gray-400">•</span>
                  )}
                </span>
                {currentFolder?.folderId === folder.folderId && (
                  <Check className="h-4 w-4" />
                )}
              </button>
            ))}

            {/* Empty State */}
            {folders.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic">
                No folders available
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Password Modal for Sensitive Operations */}
      <SensitiveNotePasswordModal
        isOpen={showPasswordModal}
        onClose={handlePasswordCancel}
        onConfirm={handlePasswordConfirm}
        operationType={pendingMove?.operationType || 'move'}
        targetFolderName={pendingMove?.folderName || ''}
        loading={isMoving}
      />
    </div>
  )
}

/**
 * Get the appropriate SVG icon for a folder type
 */
const getFolderIcon = (folderType, color = '#6B7280') => {
  const iconProps = {
    size: 14,
    className: 'flex-shrink-0'
  }

  switch (folderType) {
    case 'PINNED':
      return <Pin {...iconProps} color="#FFD700" />
    case 'SENSITIVE':
      return <Lock {...iconProps} color="#DC2626" />
    case 'CUSTOM':
    default:
      return <Folder {...iconProps} color={color} />
  }
}

export default NoteFolderDropdown 